import { identity, match } from '@frontend/duck-tape';
import { useCallback, useMemo } from '@frontend/react';
import {
  ActionIcon,
  Button,
  Icon,
  PageContainer,
  Pill,
  Table,
  Text,
  TextInput,
  Title,
  XStack,
  YStack,
} from '@frontend/web-react';
import { useAnalytics, useRetrieveClientQuery, useRetrieveReferralsQuery } from '@frontend/web/hooks';
import { createFileRoute } from '@tanstack/react-router';

const InviteFriends = () => {
  const { data: client, isLoading: isClientLoading } = useRetrieveClientQuery();
  const { data: referrals, isLoading: isReferralsLoading } = useRetrieveReferralsQuery();
  const { track } = useAnalytics();

  const referralURlWithPromo = useMemo(() => {
    if (isClientLoading || !client) return '';
    return client.referralUrl + '?promo=DBREFERRAL';
  }, [client, isClientLoading]);

  const copyReferralUrlToClipboard = useCallback(() => {
    track({ event: 'referralLinkCopied', topic: 'invite' });
    navigator.clipboard.writeText(referralURlWithPromo);
  }, [referralURlWithPromo]);

  return (
    <PageContainer
      className="bg-neutral-50"
      gap="lg"
      isLoading={isClientLoading || isReferralsLoading}
      isScrollable={false}
    >
      {/* Copy referral link and qr code section */}
      {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
      <XStack className="p-lg mt-lg w-fit min-w-[700px] rounded-lg bg-blue-950" flexShrink={1} gap="lg">
        <YStack className="gap-md">
          <XStack gap="md">
            <Icon iconClassName="stroke-lime-200 size-[45px]" name="IconGift" stroke={1.5} />
            <Title className="text-lime-200" type="h1" uppercase>
              Give the gift of Duckbill
            </Title>
          </XStack>
          <Text className="text-white">
            Your referrals get{' '}
            <Text className="font-bold italic text-white" span>
              50% off their first 2 months
            </Text>{' '}
            and prioritized access off of the waitlist!
          </Text>
          <YStack className="mt-md" gap="md">
            <Text className="text-white">Copy your own personalized referral link below, then send to a friend:</Text>
            <XStack className="gap-md">
              <TextInput
                // eslint-disable-next-line tailwindcss/no-arbitrary-value
                className="w-[312px]"
                onChange={identity}
                readOnly
                rightSection={
                  <ActionIcon onClick={copyReferralUrlToClipboard} variant="white">
                    <Icon name="IconCopy" />
                  </ActionIcon>
                }
                value={referralURlWithPromo}
              />
              <Button className="w-fit" label="Copy Link" onClick={copyReferralUrlToClipboard} />
            </XStack>
          </YStack>
        </YStack>
      </XStack>

      {/* List of sent invites */}
      <YStack gap="lg">
        <Title type="h5">Review Invites</Title>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Status</Table.Th>
            </Table.Tr>
          </Table.Thead>
          {referrals?.map((referral) => {
            const statusColor = match(referral.status)
              .with('Pending', () => 'lime')
              .with('Accepted', () => 'green')
              .with('Waitlist', () => 'blue')
              .otherwise(() => 'gray');

            return (
              <Table.Tr key={referral.referredName + referral.status}>
                <Table.Td>{referral.referredName}</Table.Td>
                <Table.Td>
                  <Pill classNames={{ label: `font-medium text-${statusColor}-900`, root: `bg-${statusColor}-200` }}>
                    {referral.status}
                  </Pill>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table>
        {!referrals?.length && (
          <YStack className="items-center" gap="lg">
            <Icon isLoading={false} name="IconMoodConfuzed" size="lg" stroke={2} />
            <Text>{"You haven't shared any invites yet"}</Text>
          </YStack>
        )}
      </YStack>
    </PageContainer>
  );
};

export const Route = createFileRoute('/app/settings/invite-friends')({
  component: InviteFriends,
});
